/**
 * Design sandbox entry — a second Vite entry point, not part of the app.
 *
 * Why this exists
 * ───────────────
 * `src/App.tsx` is a single 152-route table. Adding a route to try a layout
 * means editing the file every real page depends on, remembering to remove it,
 * and having the draft counted by `npm run a11y` and `npm run inventory:render`
 * as though it shipped. This entry sidesteps all of that.
 *
 * Why it cannot leak into production
 * ──────────────────────────────────
 * `vite.config.ts` sets no `build.rollupOptions.input`, so Vite's default
 * applies: the root `index.html` is the only entry that gets built. Nothing
 * under `design/` is reachable from it, so nothing here is bundled, and
 * react-snap (which crawls `dist`) never sees it. The dev server, by contrast,
 * serves any HTML file by path — hence `/design/` works with plain `npm run dev`.
 *
 * Nothing else is aware of this folder either: the audit scripts walk `src`,
 * and `tsconfig.app.json` includes `src`. A broken draft cannot redden a gate.
 */

import React from "react";
import { createRoot } from "react-dom/client";
import { HashRouter } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";

/* The app's real stylesheets, in the app's real order. Tokens, Tailwind and the
   design system behave here exactly as they do in `src/main.tsx`. */
import "@/styles/index.css";
import "@/styles/owen-theme.css";
import "./sandbox.css";

import { Sandbox } from "./Sandbox";

/* No retries and no refetch-on-focus: a draft that hits the network should fail
   fast and visibly rather than quietly resolving three seconds later. */
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* HashRouter, not BrowserRouter: routes live under `#/`, so the dev
          server never has to rewrite `/design/*` back to the entry. */}
      <HashRouter>
        <TooltipProvider>
          <Sandbox />
          <SonnerToaster />
        </TooltipProvider>
      </HashRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
